import { useAccountUsage, UseAccountUsageResponse } from 'api/BillingApi';
import { selectFeatureFlags } from 'core/userContext';
import { DimensionSet, OrgMetric, UsageMetricGroups } from 'modules/domains/billing';
import { addNegativeToast } from 'modules/toastContainer';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDateContext } from './DateContext';
import { QueryContext } from './QueryContext';

/** Billing Cycle Metrics */
const billingCycleAggLevels = DimensionSet.ByOrg;

export const QueryContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const { BillingWhatsAppReporting, WhatsAppMessageChannelGA } = useSelector(selectFeatureFlags);
  const isWhatsAppEnabled = BillingWhatsAppReporting && WhatsAppMessageChannelGA;

  const {
    billingCycleStart,
    billingPeriod: { endDatePST, startDatePST },
  } = useDateContext();

  const onError = () => {
    dispatch(
      addNegativeToast(
        'Trouble fetching usage data. Please try again, or contact your account manager.',
      ),
    );
  };

  /** Get valid metric groups based on feature flag availability */
  const billingPeriodMetricGroups = useMemo(() => {
    return Object.values(UsageMetricGroups)
      .filter((group) => group !== UsageMetricGroups.WhatsApp || isWhatsAppEnabled)
      .join(',');
  }, [isWhatsAppEnabled]);

  const billingPeriodAggLevels = useMemo(() => {
    const whatsAppDimensions = [
      DimensionSet.ByOrgProjectWhatsAppTemplateCategoryRecipientCountry,
      DimensionSet.ByOrgWhatsAppTemplateCategory,
    ];

    return [
      DimensionSet.ByOrg,
      DimensionSet.ByOrgBillingPartyRecipientCountry,
      DimensionSet.ByOrgProjectJourney,
      DimensionSet.ByOrgProviderBillingParty,
      ...(isWhatsAppEnabled ? whatsAppDimensions : []),
    ].join(',');
  }, [isWhatsAppEnabled]);

  /** Billing period metrics, using selected date range */
  const {
    data: {
      lastAvailableDatePST,
      projects = {},
      values: billingPeriodMetrics = {},
    } = {} as UseAccountUsageResponse,
    isError,
    isFetched,
    isLoading,
  } = useAccountUsage(
    {
      aggLevels: billingPeriodAggLevels,
      endDatePST,
      metricGroups: billingPeriodMetricGroups,
      startDatePST,
    },
    {
      enabled: !!startDatePST && !!endDatePST,
      retry: false,
      refetchOnWindowFocus: false,
      onError,
    },
  );

  const billingCycleMetricGroups = useMemo(() => {
    const baseMetricGroups = [UsageMetricGroups.Messaging, UsageMetricGroups.Sms];
    const metricGroups = isWhatsAppEnabled
      ? [...baseMetricGroups, UsageMetricGroups.WhatsApp]
      : baseMetricGroups;
    return metricGroups.join(',');
  }, [isWhatsAppEnabled]);

  /** Billing cycle metrics, from start of contract to end of selected period or last available date */
  const {
    data: { values: billingCycleMetricsBase = {} } = {} as UseAccountUsageResponse,
  } = useAccountUsage(
    {
      aggLevels: billingCycleAggLevels,
      endDatePST,
      metricGroups: billingCycleMetricGroups,
      startDatePST: billingCycleStart,
    },
    {
      enabled: !!billingCycleStart && !!endDatePST,
      retry: false,
      refetchOnWindowFocus: false,
      onError,
    },
  );

  /** Total usage metrics calculated for metrics, mainly Messaging */
  const billingCycleOrgMetrics = billingCycleMetricsBase?.ByOrg?.[0] || [];
  const billingCycleMetrics = useMemo(
    () =>
      billingCycleOrgMetrics.reduce(
        (acc, metric) => ({ ...acc, [metric.metricName]: metric }),
        {} as Record<string, OrgMetric>,
      ),
    [billingCycleOrgMetrics],
  );

  /** Org metrics */
  const orgMetrics = billingPeriodMetrics?.[DimensionSet.ByOrg]?.[0] || [];
  const orgMetricsByName = useMemo(
    () =>
      orgMetrics.reduce(
        (acc, metric) => ({ ...acc, [metric.metricName]: metric }),
        {} as Record<string, OrgMetric>,
      ),
    [orgMetrics],
  );

  return (
    <QueryContext.Provider
      value={{
        isError,
        isFetched,
        isLoading,
        lastAvailableDatePST,
        orgMetricsByName,
        projects,
        billingCycleMetrics,
        billingPeriodMetrics,
      }}
    >
      {children}
    </QueryContext.Provider>
  );
};
